import { SortableOptions } from "client/Data/sorting";
import {
	FilterValueState,
	MultiSelectableOptions,
	useEffectSkipFirstRender,
	useResizeObserver,
	useValueEqualMemo
} from "client/Hooks";
import {
	clamp,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { mergeRefs } from "client/Misc/reactUtils";
import {
	CSSProperties,
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	UIEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { ColumnFilterableFormBuilder } from "../FilterableForms/SearchFilterableForm";
import {
	LassoContainer,
	LassoMode,
	lassoModeToScopeKind
} from "../LassoContainer/LassoContainer";
import { Body } from "./Body";
import { Cell } from "./Cell";
import {
	ColumnState,
	RegularTableColumnWidth,
	TableColumnWidth,
	VirtualTableColumnWidth
} from "./Column";
import { RootContext, RootContextData } from "./contexts";
import { Header } from "./Header";
import {
	bufferSize,
	rowHeight,
	headerHeight
} from "./misc";
import { Row } from "./Row";
import styles from "./TableView.module.less";
import classNames from "classnames";
import { LoadMoreIndicator } from "./LoadMoreIndicator";
import { NoResults } from "../NoResults/NoResults";
import { FontIconName } from "../Icon/Icon";
import { FormattedTextString } from "client/Misc/commonTypes";
export type { TableViewCellProps } from "./Cell";
export type { TableViewRowProps } from "./Row";
export type { LoadMoreIndicatorProps as TableViewLoadMoreIndicatorProps } from "./LoadMoreIndicator";

export type TableViewNoResultConfig = {
	icon: FontIconName;
	title: string;
	message: string | FormattedTextString;
};

type CommonTableViewColumnDefinition = {
	title: string | ReactElement;
	minWidth?: number;
	maxWidth?: number;
	sortable?: SortableOptions;
	filterable?: FilterValueState<string> | ColumnFilterableFormBuilder | false;
	resizable?: boolean;
	isExpensiveToRender?: boolean;
	trackingId: string;
};

export type VirtualTableViewColumnDefinition =
	& CommonTableViewColumnDefinition
	& {
		width: VirtualTableColumnWidth;
	};

export type RegularTableViewColumnDefinition =
	& CommonTableViewColumnDefinition
	& {
		width?: RegularTableColumnWidth;
	};

export type TableViewColumnDefinition =
	| VirtualTableViewColumnDefinition
	| RegularTableViewColumnDefinition;

export type TableViewProps<TItem> = {
	multiSelectable?: MultiSelectableOptions | false;
	hasLassoSelection?: boolean;
	items: readonly TItem[];
	headerExtension?: ReactElement;
	itemKey: StringKeyOf<TItem> | ((item: TItem, index: number) => number | string);
	noResultConfig?: TableViewNoResultConfig;
	hasZebraPrinting?: boolean;
	shouldHideDisabledCheckboxes?: boolean;
	trackingId: string;
	renderAfterItems?: ReactElement | null;
	shouldEvaluateBackgroundColor?: boolean;
	hasNoBottomDelimiter?: boolean;
	height?: "fill" | "stretch";
	children(item: TItem, itemKey: string | number): ReactElement;
} & (
	{
		isVirtual: true;
		columns: readonly (VirtualTableViewColumnDefinition | false)[];
	} | {
		isVirtual?: false;
		columns: readonly (RegularTableViewColumnDefinition | false)[];
	}
);

const TableViewInternal = forwardRef(
	<TItem,>(props: TableViewProps<TItem>, ref: Ref<HTMLDivElement>): ReactElement => {
		const {
			multiSelectable = false,
			hasLassoSelection = true,
			isVirtual = false,
			columns: unfilteredColumns,
			items,
			itemKey,
			headerExtension,
			noResultConfig,
			hasZebraPrinting = false,
			shouldHideDisabledCheckboxes = false,
			trackingId,
			renderAfterItems = null,
			shouldEvaluateBackgroundColor = false,
			hasNoBottomDelimiter = false,
			height,
			children
		} = props;

		const columns = unfilteredColumns.filter(x => x !== false) as TableViewColumnDefinition[];
		const columnSizeDefinitions: ColumnSizeDefinition[] = columns.map(convertToColumnSizeDefinition);
		const stableColumnSizeDefinitions = useValueEqualMemo(
			(): ColumnSizeDefinition[] => columnSizeDefinitions,
			[columnSizeDefinitions]
		);

		const [columnsState, setColumnsState] = useState<readonly ColumnState[]>(
			(): readonly ColumnState[] => {
				return columnSizeDefinitions.map(convertToColumnState);
			}
		);

		const [scrollPosition, setScrollPosition] = useState<number>(0);
		const [backgroundColor, setBackgroundColor] = useState<string | null>(null);

		const refObject: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const measurementPlaceholderRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		useEffectSkipFirstRender(
			(): void => {
				setColumnsState(
					(x): readonly ColumnState[] => {
						const result: ColumnState[] = [];

						for (const column of stableColumnSizeDefinitions) {
							const existingState: ColumnState | undefined = x.find(x => x.id === column.id);
							if (isNotNil(existingState)) {
								result.push(existingState);
							} else {
								result.push(
									convertToColumnState(column)
								);
							}
						}

						return result;
					}
				);
			},
			[stableColumnSizeDefinitions]
		);

		useEffect(
			(): void=> {
				if (!refObject.current) {
					return;
				}

				if (!shouldEvaluateBackgroundColor) {
					return;
				}

				setBackgroundColor(
					findClosestBackgroundColor(
						refObject.current,
						document.body
					)
				);
			},
			[shouldEvaluateBackgroundColor]
		);

		const {
			height: containerHeight,
			width: containerWidth
		} = useResizeObserver(refObject);
		const { width: contentWidth } = useResizeObserver(measurementPlaceholderRef);
		const hasFillColumns = useMemo(
			(): boolean => {
				return columnsState.some(c => c.width === "fill");
			},
			[columnsState]
		);

		const scrollHandler = useCallback(
			(event: UIEvent<HTMLDivElement>): void => {
				if (!isVirtual) {
					// As optimization we do not track scroll position for non virtual table
					return;
				}

				const scrollTop: number = event.currentTarget
					.scrollTop;

				setScrollPosition(scrollTop);
			},
			[isVirtual]
		);

		const totalContentHeight: number = headerHeight + (rowHeight * items.length);
		const numberOfVisibleElements: number = isVirtual
			? Math.ceil((containerHeight - headerHeight) / rowHeight) + bufferSize * 2
			: items.length;

		const numberOfSkippedElements: number = isVirtual
			? clamp(
				Math.floor(scrollPosition / rowHeight) - bufferSize,
				0,
				items.length - numberOfVisibleElements
			)
			: 0;

		const gridVariables: CSSProperties = {
			"--header-height": `${headerHeight}px`,
			"--element-height": `${rowHeight}px`,
			"--skipped-height": `${numberOfSkippedElements * rowHeight}px`
		};

		const doesSupportMultiSelect: boolean = Boolean(multiSelectable);

		const gridTemplateColumns = useMemo(
			(): string => {
				let result: string = columnsState.map(getColumnTemplate)
					.join(" ");

				if (doesSupportMultiSelect) {
					result = getColumnTemplate(selectColumnState)
						+ " " + result;
				}

				result += " "
					+ (hasFillColumns
						? "auto"
						: "minmax(min-content, 1fr)"
					);

				return result;
			},
			[
				columnsState,
				doesSupportMultiSelect,
				hasFillColumns
			]
		);

		const gridStyles: CSSProperties = {
			...gridVariables,
			gridTemplateColumns,
			height: totalContentHeight
		};

		const lassoStartHandler = useCallback(
			(lassoMode: LassoMode): void => {
				if (!multiSelectable) {
					throw new Error("Cannot start lasso because table is not multiselectable");
				}

				multiSelectable.startSelection(lassoModeToScopeKind[lassoMode]);
			},
			[multiSelectable]
		);

		const lassoStopHandler = useCallback(
			(): void => {
				if (!multiSelectable) {
					return;
				}

				multiSelectable.finishSelection();
			},
			[multiSelectable]
		);


		const contextData = useMemo(
			(): RootContextData => ({
				doesSupportMultiSelect,
				contentWidth,
				containerWidth,
				hasZebraPrinting,
				shouldHideDisabledCheckboxes
			}),
			[
				doesSupportMultiSelect,
				contentWidth,
				containerWidth,
				hasZebraPrinting,
				shouldHideDisabledCheckboxes
			]
		);

		const isEmpty: boolean = items.length === 0;

		return (
			<div
				{...setTrackingId(trackingId)}
				ref={mergeRefs(refObject, ref)}
				onScroll={scrollHandler}
				className={classNames(
					styles.root,
					{
						[styles.root_noBottomDelimiter]: hasNoBottomDelimiter,
						[styles.root_stretch]: height === "stretch",
						[styles.root_fillHeight]: height === "fill"
					}
				)}
			>
				<LassoContainer
					onLassoStart={lassoStartHandler}
					onLassoStop={lassoStopHandler}
					isDisabled={!multiSelectable || !hasLassoSelection || isEmpty}
					height={isEmpty ? "auto" : "fill"}
				>
					<RootContext.Provider value={contextData}>
						<div
							className={styles.table}
							style={gridStyles}
						>
							<Header
								multiSelectable={multiSelectable}
								columnDefinitions={columns}
								columnsState={columnsState}
								onColumnsStateChange={setColumnsState}
								extension={headerExtension}
								backgroundColor={backgroundColor ?? undefined}
							/>

							<div
								ref={measurementPlaceholderRef}
								className={styles.rowsPlaceholder}
							/>

							<Body
								numberOfSkippedElements={numberOfSkippedElements}
								numberOfVisibleElements={numberOfVisibleElements}
								items={items}
								itemKey={itemKey}
								columnDefinitions={columns}
								columnsState={columnsState}
							>
								{children}
							</Body>
						</div>
						{renderAfterItems}
					</RootContext.Provider>
				</LassoContainer>
				{isEmpty && isNotNil(noResultConfig) && (
					<div className={styles.noResultContainer}>
						<NoResults
							icon={noResultConfig.icon}
							title={noResultConfig.title}
							message={noResultConfig.message}
							trackingId="no-result"
							height="fill"
						/>
					</div>
				)}
			</div>
		);
	}
);
TableViewInternal.displayName = "TableView";

type TableViewWithSubComponents = {
	<TItem,>(props: TableViewProps<TItem> & {ref ?: Ref<HTMLDivElement>; }): ReactElement;
	displayName: string;
	Row: typeof Row;
	Cell: typeof Cell;
	LoadMoreIndicator: typeof LoadMoreIndicator;
};

export const TableView = TableViewInternal as unknown as TableViewWithSubComponents;
TableView.Row = Row;
TableView.Cell = Cell;
TableView.LoadMoreIndicator = LoadMoreIndicator;

function getColumnTemplate(columnState: ColumnState): string {
	const {
		width,
		minWidth,
		maxWidth
	} = columnState;

	switch (width) {
		case "auto":
			return `minmax(${minWidth}px, max-content)`;
		case "fill":
			return typeof maxWidth === "number"
				? `minmax(${minWidth}px, ${maxWidth}px)`
				: `minmax(${minWidth}px, 1fr)`;
		default:
			return `${clamp(width, minWidth, maxWidth)}px`;
	}
}

type ColumnSizeDefinition = {
	id: string;
	width?: TableColumnWidth;
	minWidth?: number;
	maxWidth?: number;
};

export function convertToColumnSizeDefinition(column: TableViewColumnDefinition): ColumnSizeDefinition {
	return {
		id: column.trackingId,
		width: column.width,
		minWidth: column.minWidth,
		maxWidth: column.maxWidth
	};
}

export function convertToColumnState(column: ColumnSizeDefinition): ColumnState {
	return {
		id: column.id,
		width: column.width ?? "auto",
		maxWidth: column.maxWidth ?? 400,
		minWidth: column.minWidth ?? 100
	};
}

const selectColumnState: ColumnState = {
	id: "__SELECT__",
	width: 40,
	minWidth: 40
};

function findClosestBackgroundColor(target: HTMLElement, container: HTMLElement): string | null {
	const transparentColor: string = getDefaultBackgroundColor();
	let currentElement: HTMLElement | null = target;
	do {
		const elementBackgroundColor: string = window.getComputedStyle(currentElement)
			.backgroundColor;

		if (elementBackgroundColor !== transparentColor) {
			return elementBackgroundColor;
		}

		currentElement = currentElement.parentElement;

		if (!currentElement) {
			throw new Error("The target could not be found in the container");
		}
	}
	while (currentElement !== container);

	return null;
}

function getDefaultBackgroundColor(): string {
	const div: HTMLDivElement = document.createElement("div");
	document.head.appendChild(div);
	const result: string = window.getComputedStyle(div)
		.backgroundColor;

	document.head.removeChild(div);
	return result;
}